import * as React from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface FreelancerOption {
  id: string;
  nome: string;
  email: string;
  /** Vem da RPC quando `onlyActive=false`; inativos sao exibidos marcados. */
  is_active?: boolean;
}

interface FreelancerAutocompleteProps {
  value?: string;
  onValueChange: (id: string, freelancer?: FreelancerOption) => void;
  placeholder?: string;
  /** Rótulo já conhecido do `value` (evita ida ao servidor para resolver o nome). */
  selectedLabel?: string;
  /**
   * Quando `false` (padrão), inativos também aparecem, marcados como "inativo".
   * Passe `true` apenas em fluxos que devem restringir a freelancers ativos.
   */
  onlyActive?: boolean;
  disabled?: boolean;
  className?: string;
  limit?: number;
}

/**
 * Seletor de freelancer com busca SERVER-SIDE conforme o usuário digita
 * (RPC autocomplete_freelancers). Substitui os antigos seletores que
 * carregavam todos os ~5,4k freelancers no client. Não usa cache.
 */
export function FreelancerAutocomplete({
  value,
  onValueChange,
  placeholder = "Selecione o freelancer",
  selectedLabel,
  onlyActive = false,
  disabled = false,
  className,
  limit = 20,
}: FreelancerAutocompleteProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [debounced, setDebounced] = React.useState("");
  const [label, setLabel] = React.useState<string | undefined>(selectedLabel);

  React.useEffect(() => {
    setLabel(selectedLabel);
  }, [selectedLabel]);

  // Debounce do termo digitado
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);

  // Resolve o nome do value pré-selecionado (ex.: edição) quando não temos rótulo.
  React.useEffect(() => {
    let active = true;
    if (value && !label) {
      supabase
        .from("freelancers")
        .select("id,nome")
        .eq("id", value)
        .maybeSingle()
        .then(({ data }) => {
          if (active && data) setLabel(data.nome);
        });
    }
    if (!value) setLabel(undefined);
    return () => {
      active = false;
    };
  }, [value, label]);

  const { data: options = [], isFetching } = useQuery({
    queryKey: ["freelancer-autocomplete", debounced, onlyActive, limit],
    queryFn: async () => {
      // RPC criada na migração 108 — tipos gerados do Supabase ainda não a conhecem.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("autocomplete_freelancers", {
        p_search: debounced || null,
        p_limit: limit,
        p_only_active: onlyActive,
      });
      if (error) throw error;
      return (data ?? []) as FreelancerOption[];
    },
    enabled: open,
    staleTime: 30_000,
  });

  const handleSelect = (option: FreelancerOption) => {
    onValueChange(option.id, option);
    setLabel(option.nome);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", !value && "text-muted-foreground", className)}
          disabled={disabled}
        >
          <span className="truncate">{value ? label ?? "..." : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full p-0"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              <p className="py-4 text-center text-sm text-muted-foreground">
                {isFetching ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Buscando...
                  </span>
                ) : debounced ? (
                  "Nenhum freelancer encontrado."
                ) : (
                  "Digite para buscar freelancers."
                )}
              </p>
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => handleSelect(option)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === option.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="flex flex-col">
                    <span className="truncate">
                      {option.nome}
                      {option.is_active === false && (
                        <span className="ml-2 text-red-500">inativo</span>
                      )}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {option.email}
                    </span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
